import debug from 'debug';
import get from 'lodash/get';

import { ENDPOINTS, getWalletCurrencyCode, WALLET_TOKEN_MAP } from './constants';
import loadStripeConstructor from './load-stripe';
import { hideMountLoader, MOUNT_LOADER_FALLBACK_MS, prepareMountElementForStripe, showMountLoader } from './mount-loader';
import { hidePlacingOrderOverlay, showPlacingOrderOverlay } from './placing-order-overlay';
import request from './request';
import getOrCreateSessionUrls, { resetSession } from './session';
import { handleAddressChange, handleRateChange } from './shipping-update';

const debugView = debug('xo:view:cart:stripe-express-checkout');

/**
 * Mounted Stripe Express Checkout instances keyed by mount selector, so amount updates target the
 * right instance and remounts can unmount first. The cart page mounts two containers for the same
 * cart (visible cart-page wallet + header flyout); keying by selector bounds the map per container.
 *
 * @typedef {object} MountedExpressCheckout
 * @property {() => void} clearMountLoader
 * @property {object} elements
 * @property {object} expressCheckoutElement
 * @property {Element} mountElement
 */

/**
 * @typedef {object} ExpressCheckoutInitOptions
 * @property {number} [amountCents]
 * @property {function(boolean): void} [loadingCallback]
 */

/** @type {Map<(string|Element), MountedExpressCheckout>} */
const _mountedByKey = new Map();

/** @type {Map<(string|Element), Promise<{ stripe: object, elements: object, expressCheckoutElement: object } | null>>} */
const _mountInFlightByKey = new Map();

/** @type {Map<(string|Element), Element>} */
const _mountInFlightMountByKey = new Map();

/** @type {Map<(string|Element), ExpressCheckoutInitOptions>} */
const _latestMountOptionsByKey = new Map();

/**
 * Map key for a mounted instance: the selector string when given, else the resolved DOM node.
 *
 * @param {string|Element} selector
 * @param {Element} mountElement
 * @returns {string|Element}
 */
function elementsKey(selector, mountElement) {
	return typeof selector === 'string' ? selector : mountElement;
}

/**
 * Tear down a prior mount at the same selector so remounting never stacks duplicate Stripe iframes
 * or event handlers.
 *
 * @param {string|Element} key
 * @param {Element} mountElement
 * @returns {void}
 */
function unmountExisting(key, mountElement) {
	const existing = _mountedByKey.get(key);
	if (!existing) {
		return;
	}

	try {
		existing.clearMountLoader();
	} catch (err) {
		debugView('clearMountLoader at %o failed: %s', key, err?.message);
	}

	try {
		if (typeof existing.expressCheckoutElement?.unmount === 'function') {
			existing.expressCheckoutElement.unmount();
		}
	} catch (err) {
		debugView('unmount existing ECE at %o failed: %s', key, err?.message);
	}

	_mountedByKey.delete(key);

	if (mountElement?.firstChild) {
		mountElement.textContent = '';
	}
}

/**
 * @param {string|Element} key
 * @param {ExpressCheckoutInitOptions} options
 * @returns {void}
 */
function setLatestMountOptions(key, options) {
	_latestMountOptionsByKey.set(key, {
		amountCents: options?.amountCents,
		loadingCallback: options?.loadingCallback,
	});
}

/**
 * @param {string|Element} key
 * @param {ExpressCheckoutInitOptions} [fallbackOptions]
 * @returns {ExpressCheckoutInitOptions}
 */
function getLatestMountOptions(key, fallbackOptions) {
	const latest = _latestMountOptionsByKey.get(key);
	if (!latest) {
		return fallbackOptions || {};
	}
	return {
		amountCents: latest.amountCents !== undefined ? latest.amountCents : fallbackOptions?.amountCents,
		loadingCallback: latest.loadingCallback || fallbackOptions?.loadingCallback,
	};
}

/**
 * @param {string|Element} key
 * @param {string|Element} selector
 * @param {{ elements: object } | null} result
 * @returns {void}
 */
function applyLatestAmountAfterCoalescedInit(key, selector, result) {
	if (result === null) {
		return;
	}
	const { amountCents } = getLatestMountOptions(key);
	if (!Number.isInteger(amountCents) || amountCents <= 0) {
		return;
	}
	const selectorArg = typeof selector === 'string' ? selector : undefined;
	updateExpressCheckoutAmount(amountCents, selectorArg);
}

/**
 * @param {string|Element} key
 * @returns {function(boolean): void}
 */
function getLoadingCallbackForKey(key) {
	const { loadingCallback } = getLatestMountOptions(key);
	return typeof loadingCallback === 'function' ? loadingCallback : () => {};
}

/**
 * Stripe Express Checkout Element env block. Uses the deferred-intent + ConfirmationToken
 * pattern: no upfront PaymentIntent or client_secret at mount; the PaymentIntent is created once,
 * server-side, at confirm time.
 *
 * @typedef {object} RPPaymentsEnv
 * @property {string} captureMethod        `'manual' | 'automatic'` -- maps to Stripe Elements `captureMethod`.
 * @property {string} connectedAccountId   Stripe Connect account id (`acct_...`); never write this to logs.
 * @property {string} currency             Lowercase ISO 4217 snapshot; runtime prefers getWalletCurrencyCode().
 * @property {boolean} enabled
 * @property {string[]} enabledWallets     Tokens from RP gateway settings, e.g. `['apple_pay', 'link']`.
 * @property {boolean} isTestMode
 * @property {string} publishableKey
 */

/**
 * @param {string|Element} selector
 * @returns {boolean} True when a mount completed and is tracked for this selector.
 */
export function isExpressCheckoutFullyMounted(selector) {
	const mountElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
	if (!mountElement?.isConnected) {
		return false;
	}
	const key = elementsKey(selector, mountElement);
	const entry = _mountedByKey.get(key);
	return entry?.mountElement === mountElement;
}

/**
 * @param {string|Element} selector
 * @returns {boolean} True when ECE is mounted or a mount is in flight at this selector.
 */
export function isExpressCheckoutMounted(selector) {
	const mountElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
	if (!mountElement?.isConnected) {
		return false;
	}
	const key = elementsKey(selector, mountElement);
	const inFlightMount = _mountInFlightMountByKey.get(key);
	if (inFlightMount === mountElement) {
		return true;
	}
	const entry = _mountedByKey.get(key);
	return entry?.mountElement === mountElement;
}

/**
 * Initializes the Stripe Express Checkout Element when the store is eligible. No-ops (logs and
 * returns null) when the env block is missing/disabled, the mount container is absent, the
 * publishable key or connected account id is missing, or no wallets are enabled.
 *
 * @param {string} selector              CSS selector for the mount container.
 * @param {object} options
 * @param {number} options.amountCents   Cart estimated total in cents. Re-validated server-side at confirm.
 * @param {function(boolean): void} [options.loadingCallback]  Called `true` while in flight, `false` after.
 * @returns {Promise<{ stripe: object, elements: object, expressCheckoutElement: object } | null>}
 */
/**
 * Runs `callback` after any in-flight `initExpressCheckout` for `selector` settles. If nothing is
 * in flight, waits one microtask so a cart-page mount that starts in the same tick can register first.
 *
 * @param {string} selector
 * @param {() => void} callback
 * @returns {void}
 */
export function whenExpressCheckoutInitSettled(selector, callback) {
	const mountElement = document.querySelector(selector);
	if (!mountElement) {
		callback();
		return;
	}

	const runAfterInFlight = (key) => {
		if (!_mountInFlightByKey.has(key)) {
			return false;
		}
		const inFlight = _mountInFlightByKey.get(key);
		void inFlight.finally(() => callback());
		return true;
	};

	const key = elementsKey(selector, mountElement);
	if (runAfterInFlight(key)) {
		return;
	}

	queueMicrotask(() => {
		const retryElement = document.querySelector(selector);
		if (!retryElement) {
			callback();
			return;
		}
		const retryKey = elementsKey(selector, retryElement);
		if (!runAfterInFlight(retryKey)) {
			callback();
		}
	});
}

export async function initExpressCheckout(selector, options) {
	const env = get(window, 'rp_app.env.rp_payments');
	if (env?.enabled !== true) {
		debugView('rp_payments env disabled or missing -- skipping ECE mount');
		return null;
	}

	const mountElement = typeof selector === 'string' ? document.querySelector(selector) : selector;
	if (!mountElement) {
		debugView('mount element %s not found -- skipping ECE mount', selector);
		return null;
	}

	const key = elementsKey(selector, mountElement);
	setLatestMountOptions(key, options);

	const inFlightMount = _mountInFlightMountByKey.get(key);
	if (_mountInFlightByKey.has(key)) {
		const inFlightPromise = _mountInFlightByKey.get(key);
		if (inFlightMount === mountElement) {
			return inFlightPromise.then((result) => {
				applyLatestAmountAfterCoalescedInit(key, selector, result);
				return result;
			});
		}
		// dust.render replaced the mount node while the prior mount was still in flight.
		return inFlightPromise.catch(() => null).then(() => initExpressCheckout(selector, options));
	}

	const mountPromise = performInitExpressCheckout(selector, options, env, mountElement, key);
	_mountInFlightByKey.set(key, mountPromise);
	_mountInFlightMountByKey.set(key, mountElement);
	try {
		return await mountPromise;
	} finally {
		if (_mountInFlightByKey.get(key) === mountPromise) {
			_mountInFlightByKey.delete(key);
			_mountInFlightMountByKey.delete(key);
		}
	}
}

/**
 * @param {string|Element} selector
 * @param {object} options
 * @param {RPPaymentsEnv} env
 * @param {Element} mountElement
 * @param {string|Element} key
 * @returns {Promise<{ stripe: object, elements: object, expressCheckoutElement: object } | null>}
 */
async function performInitExpressCheckout(selector, options, env, mountElement, key) {
	if (!env.publishableKey || !env.connectedAccountId) {
		// Server-side eligibility already checks this; reaching here means a controller misconfiguration.
		rp_app.logger.error({
			message: 'Stripe ECE skipped: publishableKey or connectedAccountId missing from rp_payments env',
		});
		return null;
	}

	const wallets = buildWalletsConfig(env.enabledWallets);
	if (!hasAnyWallet(wallets)) {
		debugView('no wallets enabled in env.enabledWallets -- skipping ECE mount');
		return null;
	}

	const resolvedOptions = getLatestMountOptions(key, options);
	const amountCents = Number.isInteger(resolvedOptions.amountCents) ? resolvedOptions.amountCents : 0;
	if (amountCents <= 0) {
		// Mount with a 1-cent sentinel; the real total flows through elements.update before submit.
		debugView('amountCents invalid (%o) -- mounting with 1-cent sentinel', resolvedOptions.amountCents);
	}

	const stripeLoadPromise = loadStripeConstructor();
	unmountExisting(key, mountElement);
	showMountLoader(mountElement);

	let clearMountLoader = () => {};

	try {
		const Stripe = await stripeLoadPromise;
		const stripe = Stripe(env.publishableKey, { stripeAccount: env.connectedAccountId });

		const elements = stripe.elements({
			amount: amountCents > 0 ? amountCents : 1,
			captureMethod: env.captureMethod === 'automatic' ? 'automatic' : 'manual',
			currency: getWalletCurrencyCode().toLowerCase(),
			mode: 'payment',
			setupFutureUsage: 'off_session',
		});

		const expressCheckoutElement = elements.create('expressCheckout', {
			// Intentionally no `allowedShippingCountries`: Link doesn't reliably honor it and surfaces a
			// generic error. The real gate is server-side -- unsupported destinations return no rates and
			// the event is rejected, so the buyer sees "Address not supported".
			billingAddressRequired: true,
			// "Pay <name>" label; store display name isn't in the env block yet, so use a neutral fallback.
			business: { name: get(window, 'rp_app.env.dealer_name') || 'Auto Parts Store' },
			emailRequired: true,
			// One wallet per row (full-width buttons). Stripe forbids `overflow: 'never'` with maxRows > 0.
			layout: {
				maxColumns: 1,
				maxRows: 0,
				overflow: 'never',
			},
			// Placeholder line items + shipping rate, replaced in the first shippingaddresschange handler.
			// Shipping uses 1 cent (not 0) since Stripe treats 0 as "free shipping selected".
			lineItems: [
				{ amount: amountCents > 0 ? amountCents : 1, name: 'Subtotal' },
			],
			paymentMethodOrder: ['applePay', 'googlePay', 'link'],
			paymentMethods: {
				amazonPay: 'never',
				applePay: wallets.applePay,
				googlePay: wallets.googlePay,
				link: wallets.link,
				paypal: 'never',
			},
			phoneNumberRequired: false,
			shippingAddressRequired: true,
			shippingRates: [
				{ amount: 1, displayName: 'Calculating shipping…', id: 'placeholder-calculating' },
			],
		});

		clearMountLoader = wireEvents({ elements, expressCheckoutElement, key, mountElement, stripe });
		prepareMountElementForStripe(mountElement);
		expressCheckoutElement.mount(mountElement);

		if (!mountElement.isConnected) {
			clearMountLoader();
			try {
				if (typeof expressCheckoutElement.unmount === 'function') {
					expressCheckoutElement.unmount();
				}
			} catch (err) {
				debugView('unmount stale mount target failed: %s', err?.message);
			}
			return null;
		}

		_mountedByKey.set(key, {
			clearMountLoader,
			elements,
			expressCheckoutElement,
			mountElement,
		});

		return { elements, expressCheckoutElement, stripe };
	} catch (err) {
		clearMountLoader();
		hideMountLoader(mountElement);
		rp_app.logger.error({
			message: 'Stripe ECE mount failed',
			error: err?.message,
		});
		return null;
	}
}

/**
 * Pushes a new cart total into the mounted Express Checkout Element(s) without re-mounting. No-ops
 * when the ECE isn't mounted or the amount is invalid (invalid values are ignored, not forced to the
 * sentinel, so a transient bad total never resets the sheet). Only `amount` is updated -- line-item
 * breakdowns are refreshed via the shipping handlers; this updates the wallet button label only.
 *
 * @param {number} amountCents Cart total in minor units (cents).
 * @param {string} [selector]  Update only the instance at this selector; omit to update all mounted instances.
 * @returns {void}
 */
export function updateExpressCheckoutAmount(amountCents, selector) {
	if (_mountedByKey.size === 0) {
		debugView('updateExpressCheckoutAmount called before any ECE mounted -- ignoring');
		return;
	}

	const amount = Number.isInteger(amountCents) ? amountCents : Math.round(Number(amountCents));
	if (!Number.isFinite(amount) || amount <= 0) {
		debugView('updateExpressCheckoutAmount called with invalid amount %o -- ignoring', amountCents);
		return;
	}

	const targets = selector && _mountedByKey.has(selector)
		? [_mountedByKey.get(selector).elements]
		: Array.from(_mountedByKey.values()).map((entry) => entry.elements);

	targets.forEach((elements) => {
		try {
			elements.update({ amount });
		} catch (err) {
			rp_app.logger.error({
				message: 'Stripe ECE updateExpressCheckoutAmount failed',
				error: err?.message,
			});
		}
	});
}

function buildWalletsConfig(enabledWallets) {
	const config = { applePay: 'never', googlePay: 'never', link: 'never' };
	if (!Array.isArray(enabledWallets)) {
		return config;
	}
	enabledWallets.forEach((token) => {
		const entry = WALLET_TOKEN_MAP[token];
		if (!entry) {
			debugView('buildWalletsConfig: unknown wallet token %o -- skipping', token);
			return;
		}
		config[entry.key] = entry.visibility;
	});
	return config;
}

function hasAnyWallet(walletsConfig) {
	return Object.values(walletsConfig).some((v) => v !== 'never');
}

/**
 * @param {RPPaymentsEnv | null | undefined} env
 * @returns {boolean}
 */
export function isRpPaymentsExpressCheckoutEligible(env) {
	if (env?.enabled !== true) {
		return false;
	}
	if (!env.publishableKey || !env.connectedAccountId) {
		return false;
	}
	return hasAnyWallet(buildWalletsConfig(env.enabledWallets));
}

/**
 * Starts loading Stripe.js early when RP Payments express wallets may mount (header cart / cart page).
 * Safe to call multiple times; loadStripeConstructor caches the in-flight promise.
 *
 * @returns {void}
 */
export function preloadStripeJsForRpPayments() {
	const env = get(window, 'rp_app.env.rp_payments');
	if (!isRpPaymentsExpressCheckoutEligible(env)) {
		return;
	}
	loadStripeConstructor().catch((err) => {
		debugView('preloadStripeJsForRpPayments failed: %s', err?.message);
	});
}

/**
 * @param {object} params
 * @param {object} params.elements
 * @param {object} params.expressCheckoutElement
 * @param {string|Element} params.key
 * @param {Element} params.mountElement
 * @param {object} params.stripe
 * @returns {() => void}
 */
function wireEvents({ elements, expressCheckoutElement, key, mountElement, stripe }) {
	const loaderTimeoutId = window.setTimeout(() => {
		hideMountLoader(mountElement);
	}, MOUNT_LOADER_FALLBACK_MS);
	const clearMountLoader = () => {
		window.clearTimeout(loaderTimeoutId);
		hideMountLoader(mountElement);
	};

	expressCheckoutElement.on('ready', () => {
		clearMountLoader();
	});
	expressCheckoutElement.on('loaderror', () => {
		clearMountLoader();
	});

	// Stripe ECE requires a synchronous event.resolve() or the sheet won't open, so resolve immediately
	// and warm the session in parallel (ready by the time the buyer picks an address).
	expressCheckoutElement.on('click', (event) => {
		event.resolve();
		// Re-create the session per click so it's bound to the current cart (the start endpoint snapshots
		// cart contents). Reset before warming so the call below mints a fresh one.
		resetSession();
		getOrCreateSessionUrls().catch((err) => {
			// Logged-not-thrown: a start failure resurfaces from the address handler where we reject cleanly.
			rp_app.logger.error({
				message: 'Stripe ECE: failed to warm checkout session on click',
				code: err?.code,
				error: err?.message,
			});
		});
	});

	expressCheckoutElement.on('shippingaddresschange', async (event) => {
		const loadingCallback = getLoadingCallbackForKey(key);
		try {
			loadingCallback(true);
			const payload = await handleAddressChange(event);
			applyAddressChangeToEvent(elements, event, payload);
		} catch (err) {
			handleEventFailure(event, err, 'shippingaddresschange');
		} finally {
			loadingCallback(false);
		}
	});

	expressCheckoutElement.on('shippingratechange', async (event) => {
		const loadingCallback = getLoadingCallbackForKey(key);
		try {
			loadingCallback(true);
			const payload = await handleRateChange(event);
			applyRateChangeToEvent(elements, event, payload);
		} catch (err) {
			handleEventFailure(event, err, 'shippingratechange');
		} finally {
			loadingCallback(false);
		}
	});

	expressCheckoutElement.on('confirm', async (event) => {
		// Branded overlay covers the ~1.5-2.5s gap after the wallet popup closes while the backend
		// writes the order, so the cart doesn't flash back and read as broken.
		showPlacingOrderOverlay();
		try {
			const { error: submitError } = await elements.submit();
			if (submitError) {
				throw new Error(submitError.message || 'Payment details validation failed');
			}

			// `return_url` is required (Stripe error 10400) when the dealer has redirect-based methods
			// enabled, even though wallets never redirect. Bake it in, mirroring the checkout Payment Element.
			const ctResult = await stripe.createConfirmationToken({
				elements,
				params: { return_url: window.location.href },
			});
			if (ctResult.error || !ctResult.confirmationToken) {
				throw new Error(ctResult.error?.message || 'Stripe createConfirmationToken failed');
			}

			// Session already exists (warmed on click). confirmUrl hits the Node rp-payments/confirm
			// controller; rpPaymentsGatewayId is forwarded (server re-validates it) so confirm.js skips
			// its own getPaymentOptions round-trip.
			const { confirmUrl, rpPaymentsGatewayId, sessionToken } = await getOrCreateSessionUrls();

			// 1) Persist the wallet PII (email + addresses) to the order via webstore PHP (webstore
			// CSRF). Stripe only reveals this PII now, at authorization, so it cannot be saved during
			// the earlier shipping calls. Doing it here keeps it out of the completion GET's query string.
			const persistResponse = await request(ENDPOINTS.persist, {
				data: buildPersistPayload(event, sessionToken),
			});
			if (persistResponse?.error) {
				const err = new Error(persistResponse.error.message || 'Could not save your details.');
				err.code = persistResponse.error.code;
				throw err;
			}

			// 2) Complete via a Node GET carrying only opaque, non-PII identifiers (CSRF-exempt by
			// method; protected by the unguessable XO session token in confirmUrl + auth).
			const completionQuery = buildCompletionQuery(ctResult.confirmationToken.id, rpPaymentsGatewayId, event.shippingRate);
			const response = await request(confirmUrl, { method: 'GET', query: completionQuery });

			if (response?.requiresAction && response.clientSecret) {
				// 3D Secure step-up: Stripe.js opens the challenge, then we re-hit confirm with
				// `handledNextAction: true`. KNOWN LIMITATION (tracked separately): the backend doesn't
				// yet resume the PaymentIntent, so a 3DS-challenged card won't complete on retry.
				const next = await stripe.handleNextAction({ clientSecret: response.clientSecret });
				if (next.error) {
					throw new Error(next.error.message || 'Stripe authentication failed');
				}
				const retry = await request(confirmUrl, {
					method: 'GET',
					query: { ...completionQuery, handledNextAction: 'true' },
				});
				redirectFromResponse(retry);
				return;
			}

			redirectFromResponse(response);
		} catch (err) {
			if (!err?.redirected) {
				releaseExpressCheckoutBusyState(key);
				handleEventFailure(event, err, 'confirm');
				surfaceExpressCheckoutFailureToBuyer(err, 'confirm');
			} else {
				releaseExpressCheckoutBusyState(key);
			}
		}
		// Intentional: no finally hidePlacingOrderOverlay() -- on success the overlay stays up through
		// the redirect navigation so there's no flash of the cart before the confirmation page paints.
	});

	return clearMountLoader;
}

/**
 * Map a Stripe ECE address (`{ name, address: { line1, line2, city, state, postal_code, country } }`)
 * to the flat shape webstore's `CheckoutController::getAddress` expects. Returns null when there is no
 * address block so callers can fall back (billing -> shipping).
 *
 * @param {{ name?: string, address?: object } | null | undefined} walletAddress
 * @param {string} phone
 * @returns {object | null}
 */
function toWebstoreAddress(walletAddress, phone) {
	const address = walletAddress?.address;
	if (!address) {
		return null;
	}
	return {
		address: address.line1 || '',
		address2: address.line2 || '',
		city: address.city || '',
		country: address.country || '',
		name: walletAddress.name || '',
		phone: phone || '',
		postalCode: address.postal_code || '',
		state: address.state || '',
	};
}

/**
 * Build the webstore `express/persist` body from the Stripe `confirm` event: the buyer email/name and
 * full shipping + billing addresses. Billing falls back to the shipping address when the wallet omits
 * a billing block (mirrors the prior server-side `mapWalletBillingAddress` fallback).
 *
 * @param {{ billingDetails?: object, shippingAddress?: object }} event
 * @param {string} sessionToken
 * @returns {object}
 */
function buildPersistPayload(event, sessionToken) {
	const billingDetails = event.billingDetails || {};
	const phone = billingDetails.phone || '';
	const shippingAddress = toWebstoreAddress(event.shippingAddress, phone);
	const billingAddress = toWebstoreAddress(billingDetails, phone) || shippingAddress;

	return {
		billingAddress,
		customer: {
			email: billingDetails.email || '',
			fullName: billingDetails.name || '',
			phone,
		},
		sessionToken,
		shippingAddress,
	};
}

/**
 * Build the Node completion GET query: opaque, non-PII identifiers only. `request` drops empty values,
 * so an absent gatewayId/shippingRate simply lets the server resolve/keep what it already has.
 *
 * @param {string} confirmationTokenId
 * @param {number | null | undefined} gatewayId
 * @param {{ id?: string, amount?: number } | null | undefined} shippingRate
 * @returns {Record<string, string | number | undefined>}
 */
function buildCompletionQuery(confirmationTokenId, gatewayId, shippingRate) {
	const query = {
		confirmationToken: confirmationTokenId,
		gatewayId: gatewayId || undefined,
	};
	if (shippingRate?.id && Number.isFinite(shippingRate.amount)) {
		query.shippingId = shippingRate.id;
		// internal_api expects the method amount in dollars; Stripe gives the minor unit (cents).
		query.shippingAmount = (shippingRate.amount / 100).toFixed(2);
	}
	return query;
}

/**
 * Apply an address-change result to the `shippingaddresschange` event. Updates rates + totals, and
 * rejects when the rates list is empty so the sheet shows "Address not supported". A null payload
 * means an intentional short-circuit (incomplete address); resolve with no update.
 */
function applyAddressChangeToEvent(elements, event, payload) {
	if (!payload) {
		event.resolve({});
		return;
	}

	// Empty rates => destination not serviceable. Reject before elements.update to avoid an
	// inconsistent "amount updated but no rate available" state.
	if (Array.isArray(payload.shippingRates) && payload.shippingRates.length === 0) {
		event.reject();
		return;
	}

	if (Number.isInteger(payload.totalCents) && payload.totalCents > 0) {
		// Update the amount before resolving so the sheet recomputes the running total.
		elements.update({ amount: payload.totalCents });
	}

	const update = {};
	if (Array.isArray(payload.shippingRates) && payload.shippingRates.length > 0) {
		update.shippingRates = payload.shippingRates;
	}
	if (Array.isArray(payload.lineItems) && payload.lineItems.length > 0) {
		update.lineItems = payload.lineItems;
	}

	event.resolve(update);
}

/**
 * Apply a rate-change result to the `shippingratechange` event. Updates totals/line-items only --
 * MUST NOT pass shippingRates here, since empty/missing rates trip Stripe's "no shipping available"
 * path and surface as a generic "Something went wrong".
 */
function applyRateChangeToEvent(elements, event, payload) {
	if (!payload) {
		event.resolve({});
		return;
	}

	if (Number.isInteger(payload.totalCents) && payload.totalCents > 0) {
		elements.update({ amount: payload.totalCents });
	}

	const update = {};
	if (Array.isArray(payload.lineItems) && payload.lineItems.length > 0) {
		update.lineItems = payload.lineItems;
	}

	// Visibility hook for troubleshooting opaque wallet-sheet rejections. Guard rp_app.logger, which
	// can be absent at boot on minimal pages.
	if (rp_app?.logger && typeof rp_app.logger.info === 'function') {
		rp_app.logger.info({
			message: 'Stripe ECE shippingratechange resolve payload',
			selectedRateId: event?.shippingRate?.id,
			totalCents: payload.totalCents,
			update,
		});
	}

	event.resolve(update);
}

/**
 * Maps the confirm response to a navigation or throws so the caller can surface failure cleanly.
 * Shapes: { redirected, url } => navigate; { command: 'redirect', uri } => server-instructed redirect
 * (lusca CSRF failure emits this with HTTP 200, so we must surface it rather than no-op); { error } =>
 * application error; anything else => logged and treated as a generic failure.
 */
function redirectFromResponse(response) {
	if (response?.redirected && response.url) {
		window.location.assign(response.url);
		return;
	}
	if (response?.command === 'redirect' && response.uri) {
		rp_app.logger.warn({
			message: 'Stripe ECE confirm got server-instructed redirect (likely CSRF or session failure)',
			uri: response.uri,
		});
		throw new Error('Your session expired. Please refresh the page and try again.');
	}
	if (response?.error) {
		const err = new Error(response.error.message || 'Express checkout confirmation failed');
		err.code = response.error.code;
		err.body = { error: response.error };
		throw err;
	}
	rp_app.logger.error({
		message: 'Stripe ECE confirm: unrecognized response shape',
		response,
	});
	throw new Error('Could not complete the order. Please try again.');
}

/**
 * @param {string|Element} key
 * @returns {void}
 */
function releaseExpressCheckoutBusyState(key) {
	hidePlacingOrderOverlay();
	getLoadingCallbackForKey(key)(false);
	const $ = typeof globalThis.rp$ === 'function' ? globalThis.rp$ : null;
	if ($?.fn?.overlaySpinner?.remove) {
		try {
			$('body').overlaySpinner.remove();
			$('.cart-subtotal-module').overlaySpinner.remove();
			$('.cart-flyout').overlaySpinner.remove();
		} catch (removeErr) {
			debugView('overlaySpinner.remove failed: %s', removeErr?.message);
		}
	}
}

/**
 * @param {Error & { body?: { error?: { code?: string } }, code?: string }} err
 * @returns {number | null}
 */
function parseExpressCheckoutErrorCode(err) {
	const rawCode = err?.code || err?.body?.error?.code;
	if (typeof rawCode !== 'string' || !rawCode.startsWith('EXPRESS_CHECKOUT_')) {
		return null;
	}
	const suffix = rawCode.slice('EXPRESS_CHECKOUT_'.length);
	const numericCode = Number(suffix);
	return Number.isFinite(numericCode) && numericCode > 0 ? numericCode : null;
}

/**
 * Surfaces confirm failures the same way other cart checkout errors do (`?code=` banner), with alert fallback.
 *
 * @param {Error & { body?: { error?: { message?: string } }, code?: string, message?: string }} err
 * @param {string} phase
 * @returns {void}
 */
function surfaceExpressCheckoutFailureToBuyer(err, phase) {
	const message = err?.message
		|| err?.body?.error?.message
		|| 'Could not complete your order. Please try again.';

	if (phase === 'confirm') {
		const numericCode = parseExpressCheckoutErrorCode(err);
		if (numericCode !== null) {
			const params = new URLSearchParams(window.location.search);
			params.set('code', String(numericCode));
			params.set('no_redirect', '1');
			window.location.search = `?${params.toString()}`;
			return;
		}
	}

	window.alert(message);
}

function handleEventFailure(event, err, phase) {
	// `event.paymentFailed` exists on the `confirm` event; shipping events use `event.reject`. Try both.
	if (typeof event?.paymentFailed === 'function') {
		try { event.paymentFailed({ reason: 'fail' }); } catch (e) { debugView('event.paymentFailed threw: %s', e?.message); }
	} else if (typeof event?.reject === 'function') {
		try { event.reject(); } catch (e) { debugView('event.reject threw: %s', e?.message); }
	}

	rp_app.logger.error({
		message: `Stripe ECE ${phase} failed`,
		code: err?.code,
		error: err?.message,
		stack: err?.stack,
	});
}

/**
 * Test hook -- clears mounted Elements instances. Production code never needs this.
 */
export function _resetActiveElementsForTesting() {
	_mountedByKey.forEach((entry) => {
		try {
			entry.clearMountLoader?.();
		} catch (err) {
			debugView('test teardown clearMountLoader threw: %s', err?.message);
		}
		try {
			if (typeof entry.expressCheckoutElement?.unmount === 'function') {
				entry.expressCheckoutElement.unmount();
			}
		} catch (err) {
			debugView('test teardown unmount threw: %s', err?.message);
		}
	});
	_mountedByKey.clear();
	_mountInFlightByKey.clear();
	_mountInFlightMountByKey.clear();
	_latestMountOptionsByKey.clear();
}

/**
 * Test hook -- seeds a mounted Elements instance for updateExpressCheckoutAmount tests.
 *
 * @param {string} selector
 * @param {{ update: function(object): void }} elements
 */
export function _setActiveElementsForTesting(selector, elements, mountElement = null) {
	const node = mountElement || document.querySelector(selector);
	_mountedByKey.set(selector, {
		clearMountLoader: () => {},
		elements,
		expressCheckoutElement: null,
		mountElement: node,
	});
}

export default {
	initExpressCheckout,
	isExpressCheckoutFullyMounted,
	isExpressCheckoutMounted,
	isRpPaymentsExpressCheckoutEligible,
	preloadStripeJsForRpPayments,
	updateExpressCheckoutAmount,
	whenExpressCheckoutInitSettled,
};
